import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, Countdown, ChainFlow } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { SCHOLARSHIP_DATE, SCHOLARSHIP_LABEL } from '../content/data';

const WHY = [
  'Merit-based access',
  'Reduced financial barriers',
  'Access to the core DITEC learning environment',
  'Opportunity to build practical digital capability',
  'Early exposure to the digital economy',
];

export default function Scholarship() {
  const { open } = useLead();
  const { ct, settings } = useContent();
  const schDate = settings.scholarshipDate || SCHOLARSHIP_DATE;
  const schLabel = settings.scholarshipLabel || SCHOLARSHIP_LABEL;
  return (
    <div data-testid="scholarship-page">
    <SEO title="Annual Scholarship Test" description="Merit-based access to DITEC programmes — register for the next scholarship test." />
      <header className="bg-grid-dark relative overflow-hidden bg-navy text-white">
        <div className="absolute -top-40 left-[-10%] h-[480px] w-[480px] rounded-full bg-electric/25 blur-[140px]" />
        <div className="wrap relative pb-20 pt-36 sm:pb-28 sm:pt-44">
          <SectionTag dark>Annual Scholarship Test</SectionTag>
          <Reveal delay={0.05}>
            <h1 className="text-display mt-6 max-w-4xl font-semibold">{ct('scholarship.title', null) ?? <>Your Potential Should Not Be <span className="font-serif italic font-normal text-gold">Limited by Cost.</span></>}</h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              {ct('scholarship.intro', "DITEC's annual merit-based scholarship test is designed to identify capable students and create access to its programmes for selected high-performing candidates.")}
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 flex flex-wrap gap-4">
            <ArrowBtn onClick={() => open('Scholarship')} variant="gold" testId="scholarship-cta-register">Register for the Test</ArrowBtn>
            <ArrowBtn to="/programmes" variant="ghostLight" testId="scholarship-cta-programmes">Explore Programmes</ArrowBtn>
          </Reveal>
        </div>
      </header>

      {/* Countdown */}
      <section className="bg-gold py-20 text-navy sm:py-28">
        <div className="wrap text-center">
          <SectionTag center>Next DITEC Scholarship Test</SectionTag>
          <Reveal delay={0.05}><p className="mt-5 font-serif text-2xl italic sm:text-3xl">{schLabel}</p></Reveal>
          <Reveal delay={0.1} className="mt-10 flex justify-center">
            <div className="rounded-3xl bg-navy p-6 text-white sm:p-10">
              <Countdown target={schDate} dark testId="scholarship-countdown" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="mt-10"><ArrowBtn onClick={() => open('Scholarship')} variant="navy" testId="scholarship-cta-register-2">Register for the Test</ArrowBtn></Reveal>
        </div>
      </section>

      {/* Why */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-2">
          <div>
            <SectionTag>01 — Why the Scholarship Test?</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Merit opens <span className="font-serif italic font-normal text-electric">the door.</span></h2></Reveal>
          </div>
          <div className="space-y-4">
            {WHY.map((w, i) => (
              <Reveal key={w} delay={i * 0.05} className="flex touch-target items-center gap-4 rounded-2xl border border-navy/10 bg-white px-6 py-4">
                <span className="font-mono text-xs text-electric">0{i + 1}</span>
                <span className="text-sm font-semibold tracking-tight sm:text-base">{w}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>02 — How It Works</SectionTag>
          <Reveal delay={0.08} className="mt-10">
            <ChainFlow steps={['Register', 'Prepare', 'Take the Test', 'Results', 'Scholarship Selection', 'Programme Onboarding']} testId="scholarship-flow" />
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-3xl rounded-2xl border border-navy/10 bg-paper p-6 text-sm leading-relaxed text-navy/65">
              The final eligibility rules, scholarship percentages and test logistics are published upon official confirmation. Register your interest and the DITEC team will share confirmed details with you directly.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
